import { QueryClient } from '@tanstack/react-query';

import { ApiError } from '@/modules/common/api/errors/api-error';

const DEFAULT_QUERY_RETRY_COUNT = 3;

/**
 * Default freshness window. This is what makes `refetchOnWindowFocus` affordable:
 * without it every query is stale on arrival, so alt-tabbing to Slack and back
 * would refetch everything mounted.
 */
const DEFAULT_STALE_TIME_MS = 30_000;

/**
 * The app's single TanStack QueryClient.
 *
 * It lives here rather than in `main.tsx` on purpose. The root state machine needs it for
 * `signOut(queryClient)`, and importing it from the entry file put `main.tsx` inside an import
 * cycle:
 *
 *   main.tsx → @/modules/common → components/index → app-router → root-context
 *            → root-state-machine → @/main
 *
 * A cycle through the entry file is not just untidy: Vite HMR refuses to patch modules inside it
 * and falls back to a full reload, and during that reload the entry evaluates while the barrel is
 * still initializing ("Cannot access 'AppRouter' before initialization"). `createRoot` has already
 * run by then, so React ends up with a half-mounted tree and reports
 * "Failed to execute 'removeChild'" plus "useRootContext must be used within RootContextProvider".
 * Keep this module free of app-barrel imports so the entry stays a leaf.
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // With this off, a mutation's `invalidateQueries` was the only moment the app
      // ever re-read the server — and it can silently refetch nothing, leaving the
      // screen on an hours-old snapshot until a manual reload. staleTime-gated, so
      // queries opting out (`staleTime: Infinity`) are untouched.
      refetchOnWindowFocus: true,
      staleTime: DEFAULT_STALE_TIME_MS,
      // Do not retry on 401 — the axios response interceptor already handles
      // session death (toast + logout) on the first 401, so retrying would
      // just flood Cognito/the backend before logout completes.
      retry: (failureCount, error) => {
        if (error instanceof ApiError && error.httpStatus === 401) return false;
        return failureCount < DEFAULT_QUERY_RETRY_COUNT;
      },
    },
  },
});
